<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { createUser } from "@directus/sdk";
import { FormKitMessages } from "@formkit/vue";
import { type FormKitNode } from "@formkit/core";
import type { MembershipFields } from "~/components/forms/Registration.vue";

const router = useRouter();

useHead({
  title: "Register",
});

const { data: memberships } = await useAsyncData(() => useDirectus(readItems("memberships")), { default: () => [] });

type MembershipResponse = (typeof memberships.value)[0];

const { individuals, corporates } = memberships.value.reduce(
  (acc, el) => {
    if (el.product_name !== "corporate") {
      acc.individuals.push(el);
    } else {
      acc.corporates.push(el);
    }
    return acc;
  },
  { individuals: [] as MembershipResponse[], corporates: [] as MembershipResponse[] }
);

individuals.sort((a: any, b: any) => a.amount - b.amount);
corporates.sort((a: any, b: any) => a.amount - b.amount);

const seatCapArray = corporates.map((option: any) => {
  return option.seat_cap;
});

const user = useUser();
const registerStep = ref<"intro" | "submission">("intro");
const membershipType = ref<MembershipType | undefined>(undefined);
const membershipTier = ref<MembershipCorporateTier | undefined>(undefined);

const errorsEl = ref<HTMLElement | null>(null);
const pending = ref(false);
const submit = async (fields: MembershipFields & { email: string }, node: FormKitNode) => {
  pending.value = true;
  try {
    await useDirectus(
      createUser({
        first_name: fields["first-name"],
        last_name: fields["last-name"],
        email: fields.email,
        password: fields.password,
        email_notifications: fields.email_notifications,
      })
    );

    const loginResponse = await useLogin(fields.email, fields.password);

    if (loginResponse.value?.user?.id && loginResponse.value?.profile?.id) {
      if (fields.account_type === "individual") {
        await useDirectus(
          updateItem("user_profiles", loginResponse.value?.profile?.id, {
            phone: fields.phone,
            suburb: fields.suburb,
            street_1: fields.street1,
            street_2: fields.street2,
            city: fields.city,
            state: fields.state,
            postcode: fields.postcode,
            emergency_contact_name: fields.emergency_contact_name,
            emergency_contact_relationship: fields.emergency_contact_relationship,
            emergency_contact_number: fields.emergency_contact_number,
            health_information: fields.health_information,
            dietary_requirements: fields.dietary_requirements,
          })
        );
      } else if (fields.account_type === "corporate") {
        await useDirectus(
          updateItem("user_profiles", loginResponse.value?.profile?.id, {
            organisation_name: fields.organisation_name,
            organisation_street_1: fields.organisation_address_street1,
            organisation_street_2: fields.organisation_address_street2,
            organisation_city: fields.organisation_address_city,
            organisation_country: fields.organisation_address_country,
            organisation_postcode: fields.organisation_address_postcode,
            organisation_suburb: fields.organisation_address_suburb,
            secondary_contact_email: fields.secondary_contact_email,
            secondary_contact_name: fields.secondary_contact_name,
            secondary_contact_phone: fields.secondary_contact_phone,
            phone: fields.phone,
          })
        );
      }
    }
    const user = useUser();

    await useGetUser();

    if (user.value.user?.owner) {
      router.replace("/account");
    } else {
      router.replace(`/membership?type=${fields.account_type}${fields.account_tier ? `&tier=${fields.account_tier}` : ""}`);
    }
  } catch (e: any) {
    if (e.originalError?.errors) {
      node.setErrors(
        [],
        e.originalError.errors.reduce((acc: any, cur: any) => {
          if (cur.extensions?.field !== undefined) {
            acc[cur.extensions.field] = useDirectusError(cur);
          }
          return acc;
        }, {})
      );
    } else {
      console.error(e);
      node.setErrors(["An error occurred. Please try again later."]);
    }
    nextTick(() => {
      document.querySelector(".main")?.scrollIntoView({ behavior: "smooth" });
    });
  }

  pending.value = false;
};

watch(registerStep, () => {
  document.querySelector(".main")?.scrollIntoView({ behavior: "smooth" });
});

const handleIndividualClick = (individual: MembershipResponse) => {
  membershipType.value = individual.product_name as MembershipType;
  membershipTier.value = undefined;
  if (user.value.data !== null) {
    console.log("user", user.value);
    router.replace(`/membership?type=${membershipType.value}`);
  } else {
    registerStep.value = "submission";
  }
};

const handleCorporateClick = (corporate: MembershipResponse) => {
  membershipType.value = "corporate";
  membershipTier.value = corporate.tier as MembershipCorporateTier;
  registerStep.value = "submission";
  if (user.value.data !== null) {
    router.replace(`/membership?type=${membershipType}&tier=${membershipTier}`);
  } else {
    registerStep.value = "submission";
  }
};
</script>

<template>
  <div class="bg-white">
    
<FormKitLazyProvider config-file="true">
<CommonPageBanner :local-asset="true" image="/banner-tasmanian-coastlines.jpg" size="small" class="bg-accentDark">
      <div class="font-script text-3xl font-bold text-white">Wildcare</div>
      <h1 class="text-4xl font-semibold md:text-6xl xl:text-banner">Become a member</h1>
    </CommonPageBanner>
    <CommonContainer class="main py-[--y-margin]">
      <Transition name="slide-fade" mode="out-in">
        <div v-if="registerStep === 'intro'" class="mx-auto space-y-16 xl:max-w-[90%]">
          <div class="space-y-4 border-b border-gray-400/50 pb-16">
            <h3 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">
              Thank You For Your Interest In Wildcare Tasmania!
            </h3>

            <p class="text-center text-xl font-semibold text-primary lg:text-left xl:text-2xl">
              Browse our personal and corporate membership options below to see what might work for you!
            </p>
            <p class="text-center text-xl font-semibold text-primary lg:text-left xl:text-2xl">
              If you have any questions, please don't hesitate to
              <NuxtLink class="text-secondary underline hover:no-underline hover:brightness-75" to="/contact"> reach out to us</NuxtLink>.
            </p>
          </div>

          <div class="lg:pb-16">
            <div class="space-y-4">
              <h3 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">For Individuals and Families</h3>
              <p class="text-center text-xl font-semibold text-primary lg:text-left xl:text-2xl">
                These membership options are ideal for those who want to support Wildcare as individuals, either with a yearly subscription
                or a one-off lifetime membership fee.
              </p>
              <p class="text-center text-xl font-semibold text-primary lg:text-left xl:text-2xl">
                Each of these options also allow you to subscribe additional members including youths (under 18 years) through your account,
                once your membership is active and you are logged in.
              </p>
            </div>
            <div class="flex flex-col gap-8 pt-12 lg:flex-row lg:justify-around lg:gap-0">
              <button
                v-for="individual in individuals"
                :key="individual.id"
                class="flex grow flex-col items-center space-y-4 rounded p-8 transition-colors hover:bg-tertiary focus:bg-tertiary active:bg-tertiary lg:space-y-6"
                @click="handleIndividualClick(individual)"
              >
                <img
                  class="size-32 rounded-full object-cover ring-4 ring-secondary"
                  :src="`/tier-logos/${individual.product_name}.jpg`"
                  alt=""
                />
                <div class="text-2xl capitalize text-accentDark">
                  {{ individual.product_name }}
                </div>
                <div class="font-script text-2xl text-accentDark">
                  ${{ individual.amount / 100 }}
                  {{ individual.product_name === "lifetime" ? "once off" : "per year" }}
                </div>
              </button>
            </div>
          </div>

          <div class="lg:pb-16">
            <div class="space-y-4">
              <h3 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">For Corporate Partnerships</h3>
              <p class="text-center text-xl font-semibold text-primary lg:text-left xl:text-2xl">
                If your organisation would like to support Wildcare Tasmania and the important work of our volunteer members, we invite you
                to become a Wildcare Corporate Partner. We offer several tiers of partnership, based on the size of your organisation.
              </p>
              <p class="text-center text-xl font-semibold text-primary lg:text-left xl:text-2xl">
                Our Wildcare Corporate Partnerships are open to businesses and organisations of all sizes, across all sectors. The program
                provides a unique opportunity to be part of the valuable work of Wildcare through volunteering opportunities, workplace
                WildTalks, and more.
              </p>
            </div>
            <div class="flex flex-col gap-8 pt-12 lg:flex-row lg:gap-0">
              <button
                v-for="(corporate, index) in corporates"
                :key="corporate.id"
                class="flex grow flex-col items-center space-y-4 rounded p-8 transition-colors hover:bg-tertiary focus:bg-tertiary active:bg-tertiary lg:space-y-6"
                @click="handleCorporateClick(corporate)"
              >
                <img class="size-32 rounded-full object-cover ring-4 ring-secondary" :src="`/tier-logos/${corporate.tier}.jpg`" alt="" />
                <div class="text-2xl capitalize text-accentDark">
                  {{ corporate.tier }}
                </div>
                <div class="text-center text-lg">
                  <div v-if="index === 0">For organisations with 0 - {{ seatCapArray[index] }} staff</div>
                  <div v-else-if="index < seatCapArray.length - 1">
                    For organisations with {{ seatCapArray[index - 1] + 1 }} to {{ seatCapArray[index] }} staff
                  </div>
                  <div v-else>For organisations with {{ seatCapArray[index - 1] + 1 }}+ staff</div>
                </div>
                <div class="font-script text-2xl text-accentDark">${{ corporate.amount / 100 }} per year</div>
              </button>
            </div>
          </div>
        </div>
        <div v-else-if="registerStep === 'submission'" class="mx-auto xl:max-w-[70%]">
          <h3 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">Let's get started!</h3>
          <FormsRegistration :initial="{ account_type: membershipType, account_tier: membershipTier }" :pending="pending" @submit="submit">
            <template #preForm>
              <FormKitMessages ref="errorsEl" class="col-span-2 my-4 scroll-mt-32 border-2 border-red-700 bg-red-50 p-4" role="alert" />
              <FormKit
                id="email"
                type="text"
                name="email"
                label="Email"
                placeholder="Please enter your email address"
                validation="required|email"
                prefix-icon="email"
                prefix-icon-class="text-xl"
              />
            </template>

            <template #submit>
              <div class="flex flex-row justify-between">
                <CommonButtonNuxtLink
                  btn-style="primary"
                  btn-hover-style="secondary"
                  class="mt-16"
                  type="button"
                  @click="registerStep = 'intro'"
                >
                  Back
                </CommonButtonNuxtLink>
                <CommonButtonNuxtLink :disabled="pending" btn-style="primary" btn-hover-style="secondary" class="mt-16" type="submit">
                  Next
                </CommonButtonNuxtLink>
              </div>
            </template>
          </FormsRegistration>
        </div>
      </Transition>
    </CommonContainer>
</FormKitLazyProvider>

  </div>
</template>

<style>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease-out;
}

.slide-fade-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
